import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getPurchaseOrders, deletePurchaseOrder } from '../../../models/requisitionModel';
import { toast } from 'react-toastify';

function PurchaseOrdersList ({ status = 'completed' }) {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [showPendingApproval, setShowPendingApproval] = useState(false);
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Filter purchase orders by workflow stage
  const filterByWorkflowStage = (purchaseOrders, stage) => {
    if (!stage || stage === 'all' || stage === 'pending') return purchaseOrders;
    
    return purchaseOrders.filter(req => {
      const workflowStage = getWorkflowStage(req);
      return workflowStage === stage;
    });
  };

  // Fetch purchase orders
  const fetchPurchaseOrders = useCallback(async () => {
    setLoading(true);
    try {
      const effectiveStatus = getEffectiveStatus();
      const params = {
        page: currentPage,
        pageSize: pageSize,
        status: effectiveStatus
      };
      
      if (searchTerm) params.filter = searchTerm;
      
      // Use server-side filtering for pending approvals
      if (showPendingApproval) {
        params.pendingApproval = true;
      }
      
      const response = await getPurchaseOrders(params);
      
      if (response.status === 1 && response.data) {
        let responseData = response.data.data || [];
        const meta = response.data.meta || {};
        
        // Server-side filtering now handles pending approvals
        if (showPendingApproval) {
          // Data is already filtered by server for pending approvals
          setPendingApprovalCount(responseData.length);
          setPurchaseOrders(responseData);
          setTotalCount(responseData.length);
        } else {
          // Apply client-side workflow stage filtering
          const effectiveStatus = getEffectiveStatus();
          if (['draft', 'quotation', 'evaluation', 'declaration', 'first_approval', 
               'second_approval', 'third_approval', 'buyer_assignment', 'sap_approval', 
               'purchase_order', 'completed', 'rejected'].includes(effectiveStatus)) {
            responseData = filterByWorkflowStage(responseData, effectiveStatus);
          }
          
          // Count requisitions waiting for the current user's approval (for display purposes)
          if (user && user.user_id) {
            // Client-side count for notification badge (when not in pending approval mode)
            const pendingApproval = responseData.filter(req => {
                const hasNoErrors = 
                    req.requisition_quote && 
                    (req.quotation_evaluation !== "" && req.quotation_evaluation !== null) && 
                    (req.requisition_declaration !== "" && req.requisition_declaration !== null);
                
                const currentUserId = String(user.user_id);
                
                const needsUserApprovalNow = 
                  (String(req.mid) === currentUserId && req.requisition_status === 0) ||
                  (String(req.second) === currentUserId && req.requisition_status === 1) ||
                  (String(req.approval) === currentUserId && req.requisition_status === 2) ||
                  (req.mid_delegate && String(req.mid_delegate) === currentUserId && req.requisition_status === 0) ||
                  (req.second_delegate && String(req.second_delegate) === currentUserId && req.requisition_status === 1) ||
                  (req.sap_delegate && String(req.sap_delegate) === currentUserId && req.requisition_status === 2);
                  
                const notFullyApproved = req.requisition_status < (req.specification_approvals || 1);
                
                return hasNoErrors && needsUserApprovalNow && notFullyApproved;
            });
            
            setPendingApprovalCount(pendingApproval.length);
          }
          
          setPurchaseOrders(responseData);
          const actualTotal = responseData.length; // Use filtered count for workflow stages
          setTotalCount(actualTotal);
        }
      } else {
        console.error('Failed API response:', response);
        toast.error(response.message || 'Failed to load purchase orders');
        setPurchaseOrders([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('An error occurred while loading purchase orders');
      setPurchaseOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, status, user, showPendingApproval, location.search]);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [fetchPurchaseOrders]);

  // Reset to page 1 when search changes or when switching between approval modes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showPendingApproval]);
  
  // Check for "ready" parameter in URL, similar to PHP version
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const readyParam = params.get('ready');
    if (readyParam) {
      setShowPendingApproval(true);
    } else {
      setShowPendingApproval(false);
      
      // If no specific status is set in URL, redirect to completed status on initial load
      const urlStatus = params.get('status');
      if (!urlStatus && location.pathname.includes('/dashboard/requisitions/po')) {
        navigate(`/dashboard/requisitions/po?status=completed`, { replace: true });
      }
    }
  }, [location.search, navigate]);

  // Get effective status from URL params or prop
  const getEffectiveStatus = () => {
    const params = new URLSearchParams(location.search);
    const urlStatus = params.get('status');
    // Default to 'completed' if no status is specified
    return urlStatus || status || 'completed';
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const formatDate = (timestamp) => {
    if (!timestamp || timestamp === null || timestamp === '0' || timestamp === 0) return 'N/A';
    
    // If it's already in YYYY-MM-DD format, convert to display format
    if (typeof timestamp === 'string' && timestamp.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
    }
    
    // Handle numeric timestamps
    const date = Number.isInteger(parseInt(timestamp))
      ? new Date(parseInt(timestamp) * 1000)
      : new Date(timestamp);
      
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    // Check if it's the Unix epoch (1970-01-01) which indicates invalid data
    if (date.getFullYear() === 1970 && date.getMonth() === 0 && date.getDate() === 1) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-GB');
  };

  // Get current workflow stage based on ViewRequisition logic
  const getWorkflowStage = (req) => {
    // Check for rejected first
    if (req.requisition_status === 4) {
      return 'rejected';
    }
    
    // Check if PO is issued (completion)
    if (req.requisition_po_date > 0) {
      return 'completed';
    }
    
    // Check if approved and assigned for SAP approval
    if (req.requisition_status >= (req.specification_approvals || 1)) {
      if (req.requisition_sap_approve) {
        return 'purchase_order'; // Waiting for PO
      } else if (req.assigned) {
        return 'sap_approval'; // Waiting for SAP approval
      } else {
        return 'buyer_assignment'; // Waiting for buyer assignment
      }
    }
    
    // Check approval stages
    if (req.requisition_status >= 3 && req.specification_approvals >= 3) {
      return 'buyer_assignment'; // Third approval completed
    }
    if (req.requisition_status === 2 && req.specification_approvals >= 3) {
      return 'third_approval';
    }
    if (req.requisition_status === 2 && req.specification_approvals <= 2) {
      return 'buyer_assignment';
    }
    if (req.requisition_status === 1) {
      return 'second_approval';
    }
    
    // Check if ready for first approval
    const hasQuotation = req.specification_emergency == 1 
      ? (req.requisition_quote && req.submissions && req.submissions.length > 0)
      : (req.submissions && req.submissions.length > 0);
    
    const hasEvaluation = req.selected && (
      (req.selected.values && Array.isArray(req.selected.values) && req.selected.values.length > 0) ||
      (req.selected.values && typeof req.selected.values === 'object' && 
       (req.selected.values.quality || req.selected.values.adherence || req.selected.values.delivery || req.selected.values.price)) ||
      (req.selected.quotation_evaluation && req.selected.quotation_evaluation !== null && 
       req.selected.quotation_evaluation !== '' && req.selected.quotation_evaluation !== 'null' &&
       (req.selected.quotation_evaluation.startsWith('[') || req.selected.quotation_evaluation.startsWith('{')))
    );
    
    const hasDeclaration = req.requisition_declaration && req.requisition_declaration !== '';
    
    if (req.requisition_status === 0) {
      if (hasDeclaration && hasEvaluation && hasQuotation) {
        return 'first_approval';
      } else if (!hasDeclaration && hasEvaluation && hasQuotation) {
        return 'declaration';
      } else if (!hasEvaluation && hasQuotation) {
        return 'evaluation';
      } else if (!hasQuotation) {
        return 'quotation';
      }
    }
    
    // Check for draft conditions
    if (!req.requisition_quote || req.requisition_quote === '' ||
        !req.quotation_evaluation || req.quotation_evaluation === '' ||
        !req.requisition_declaration || req.requisition_declaration === '' ||
        (req.specification_emergency === 1 && (!req.files || req.files === null))) {
      return 'draft';
    }
    
    return 'pending';
  };

  const getStatusDisplay = (req) => {
    const workflowStage = getWorkflowStage(req);
    
    // Check if user needs to approve this requisition
    let needsUserApproval = false;
    if (user && req.requisition_status < (req.specification_approvals || 1)) {
      const currentUserId = String(user.user_id);
      const hasNoErrors = 
        req.requisition_quote && req.requisition_quote !== "" &&
        req.quotation_evaluation && req.quotation_evaluation !== "" &&
        req.requisition_declaration && req.requisition_declaration !== "";
      
      needsUserApproval = hasNoErrors && (
        (String(req.mid) === currentUserId && req.requisition_status === 0) ||
        (String(req.second) === currentUserId && req.requisition_status === 1) ||
        (String(req.approval) === currentUserId && req.requisition_status === 2) ||
        (req.mid_delegate && String(req.mid_delegate) === currentUserId && req.requisition_status === 0) ||
        (req.second_delegate && String(req.second_delegate) === currentUserId && req.requisition_status === 1) ||
        (req.sap_delegate && String(req.sap_delegate) === currentUserId && req.requisition_status === 2)
      );
    }
    
    // Override with user-specific message if needed
    if (needsUserApproval) {
      return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-red-100 text-red-800">Needs Your Approval</span>;
    }
    
    // Return styled badge based on workflow stage
    switch (workflowStage) {
      case 'completed':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-green-100 text-green-800">PO Issued</span>;
      case 'purchase_order':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-blue-100 text-blue-800">Awaiting PO</span>;
      case 'sap_approval':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-purple-100 text-purple-800">SAP Approval</span>;
      case 'buyer_assignment':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-indigo-100 text-indigo-800">Buyer Assignment</span>;
      case 'third_approval':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-yellow-100 text-yellow-800">Third Approval</span>;
      case 'second_approval':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-yellow-100 text-yellow-800">Second Approval</span>;
      case 'first_approval':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-yellow-100 text-yellow-800">First Approval</span>;
      case 'declaration':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-orange-100 text-orange-800">Declaration</span>;
      case 'evaluation':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-orange-100 text-orange-800">Evaluation</span>;
      case 'quotation':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-orange-100 text-orange-800">Quotation</span>;
      case 'rejected':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-red-100 text-red-800">Rejected</span>;
      case 'draft':
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-gray-100 text-gray-800">Draft</span>;
      default:
        return <span className="inline-block px-3 py-1 rounded font-semibold text-xs bg-blue-100 text-blue-800">Pending</span>;
    }
  };

  if (loading && purchaseOrders.length === 0) {
    return (
      <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 py-1">
      {/* Professional Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between">
          {/* Left: Icon, Title, Description, Results */}
          <div className="flex items-start">
            <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <svg className="h-5 w-5" fill="none" stroke="#0096DE" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                {showPendingApproval ? 'Ready for Approval' : 'Purchase Orders'}
              </h1>
              <p className="text-xs text-gray-500 mb-1">
                Issued Purchase Orders
              </p>
              <div className="flex items-center mt-1">
                <span className="text-xs font-medium" style={{ color: '#0096DE' }}>
                  {totalCount ? `Showing ${Math.min(pageSize, totalCount)} of ${totalCount} results` : '0 results'}
                  {(searchTerm || (status && status !== 'pending') || showPendingApproval) && (
                    <span className="ml-1 text-blue-600">
                      (filtered)
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
          
          {/* Right: All Controls and Search */}
          <div className="flex flex-col gap-2 items-end min-w-0">
            {/* Row 1: All Controls on Right */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Clear Filters Button (only show if filters are active) */}
              {(searchTerm || (status && status !== 'pending') || showPendingApproval) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    navigate('/requisitions');
                  }}
                  className="inline-flex items-center px-2 py-1.5 border border-red-300 rounded-md shadow-sm text-xs text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-500 flex-shrink-0"
                >
                  Clear Filters
                </button>
              )}
              
              {/* Refresh Button */}
              <button 
                onClick={() => fetchPurchaseOrders()} 
                className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                title="Refresh"
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0096DE' : 'currentColor'} style={{ color: loading ? '#0096DE' : 'currentColor' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              
              {/* Status Filter */}
              <div className="flex-shrink-0">
                <select
                  value={showPendingApproval ? "ready" : getEffectiveStatus()}
                  onChange={(e) => {
                    if (e.target.value === "ready") {
                      navigate(`/dashboard/requisitions?ready=${pendingApprovalCount}`);
                    } else if (e.target.value === "pending") {
                      navigate(`/dashboard/requisitions?status=pending`);
                    } else if (e.target.value === "all") {
                      navigate(`/dashboard/requisitions?status=all`);
                    } else {
                      navigate(`/dashboard/requisitions/po?status=${e.target.value}`);
                    }
                  }}
                  className={`text-sm px-2 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    (showPendingApproval || (status && status !== 'pending')) ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white'
                  }`}
                >
                  {showPendingApproval && (
                    <option value="ready">Ready for my approval</option>
                  )}
                  
                  {/* Purchase Order Specific Filters */}
                  <option value="all">All Purchase Orders</option>
                  <option value="issued">Issued POs</option>
                  <option value="ready_for_po">Ready for PO Creation</option>
                  
                  {/* Standard States */}
                  <option value="pending">Pending</option>
                  <option value="completed">Completed (PO Issued)</option>
                  
                  {!showPendingApproval && pendingApprovalCount > 0 && (
                    <option value="ready">Ready for my approval ({pendingApprovalCount})</option>
                  )}
                </select>
              </div>

              {/* Page Size Filter */}
              <div className="flex-shrink-0">
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 30, 50].map(size => (
                    <option key={size} value={size}>{size} per page</option>
                  ))}
                </select>
              </div>

              {/* Create Button */}
              {can('Create Requisition') && (
                <Link
                  to="/dashboard/specifications"
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1 flex-shrink-0"
                  style={{ backgroundColor: '#0096DE', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: '#0096DE' }}
                >
                  <svg className="-ml-0.5 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  New Requisition
                </Link>
              )}
            </div>
            
            {/* Search Box in Row 2 */}
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search purchase orders..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action - Awaiting Approval */}
      {pendingApprovalCount > 0 && !showPendingApproval && (
        <div className="flex items-center bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-4">
          <div className="w-2 h-2 bg-amber-400 rounded-full mr-3 animate-pulse"></div>
          <span className="text-amber-700 text-sm font-medium mr-3">
            {pendingApprovalCount} purchase order{pendingApprovalCount > 1 ? 's' : ''} ready for your approval now
          </span>
          <Link
            to={`?ready=${pendingApprovalCount}`}
            className="inline-flex items-center px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-medium text-sm rounded-md transition-colors shadow-sm"
          >
            Review Now
            <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
      )}
      
      {showPendingApproval && (
        <div className="flex items-center bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 mb-4">
          <span className="text-blue-700 text-sm font-medium mr-3">
            Showing {pendingApprovalCount} purchase order{pendingApprovalCount > 1 ? 's' : ''} ready for your approval
          </span>
          <Link
            to="/dashboard/requisitions/po"
            className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-md transition-colors shadow-sm"
          >
            View All
            <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
      )}
      
      {/* Simple Table */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>ID</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Req Number</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Project Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>User</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Department</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Created</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Supplier</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Status</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan="9" className="px-3 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading...</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && purchaseOrders.length === 0 && (
                <tr>
                  <td colSpan="9" className="px-3 py-6 text-center">
                    <p className="text-gray-600">
                      {showPendingApproval 
                        ? "No purchase orders require your approval at this time" 
                        : "No purchase orders found"}
                    </p>
                  </td>
                </tr>
              )}
              {!loading && purchaseOrders.map(req => {
                const displayUser = req.user_full_name || req.requester_name || (req.user_id ? `User #${req.user_id}` : 'Unknown');
                
                // Match the original PHP logic: only show actual supplier names, otherwise "None"
                let displaySupplier = 'None';
                if (req.quotation_company_name && req.quotation_company_name !== 'Not Selected' && !req.quotation_company_name.startsWith('Supplier ID:')) {
                  displaySupplier = req.quotation_company_name;
                } else if (req.company_name_actual && req.company_name_actual !== 'Not Selected' && !req.company_name_actual.startsWith('Supplier ID:')) {
                  displaySupplier = req.company_name_actual;
                }
                
                return (
                  <tr key={req.requisition_id} className="hover:bg-blue-50">
                    <td className="px-3 py-2 text-sm">{req.requisition_id}</td>
                    <td className="px-3 py-2 text-sm">EPR-{req.requisition_number}</td>
                    <td className="px-3 py-2 text-sm">{req.specification_title || 'Untitled Project'}</td>
                    <td className="px-3 py-2 text-sm">{displayUser}</td>
                    <td className="px-3 py-2 text-sm">{req.department_name || 'General'}</td>
                    <td className="px-3 py-2 text-sm">{formatDate(req.requisition_date)}</td>
                    <td className="px-3 py-2 text-sm">{displaySupplier}</td>
                    <td className="px-3 py-2 text-sm">{getStatusDisplay(req)}</td>
                    <td className="px-3 py-2 text-sm">
                      <Link
                        to={`/dashboard/requisitions/${req.requisition_id}`}
                        className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Simple Pagination */}
        {totalCount > 0 && (
          <div className="bg-gray-50 px-3 py-2 border-t border-gray-200 flex items-center justify-between text-xs">
            <div className="text-gray-500">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                Previous
              </button>
              <span className="px-2 py-1 border border-gray-300 bg-white text-gray-700 font-medium text-xs">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PurchaseOrdersList;
